import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { user, login, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user?.role === "admin") nav("/admin", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") { await logout(); setError("This account does not have admin access."); return; }
      nav(loc.state?.from || "/admin", { replace: true });
    } catch (e2) { setError(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a1420] grid place-items-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "32px 32px" }} />
      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="font-display font-black text-3xl"><span className="text-white">YourRace</span><span className="text-[#22d3ee]">Finder</span></div>
          <div className="inline-flex items-center gap-1.5 mt-2 text-xs font-display font-semibold tracking-[0.25em] text-[#22d3ee]"><ShieldCheck size={14} /> ADMIN CONTROL CENTRE</div>
        </div>
        <div className="bg-navy-card border border-slate-800 rounded-2xl p-8">
          <h1 className="font-display font-extrabold uppercase text-xl text-white">Admin Login</h1>
          <p className="text-slate-400 text-sm mt-1">Private access — YourRaceFinder staff only.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-3 rounded-lg" data-testid="admin-login-error">{error}</div>}
            <div>
              <label className="text-sm font-semibold text-slate-300">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
                className="mt-1 w-full bg-[#0a1420] border border-slate-700 text-white rounded-lg px-4 py-3 focus:border-[#22d3ee] outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-300">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
                className="mt-1 w-full bg-[#0a1420] border border-slate-700 text-white rounded-lg px-4 py-3 focus:border-[#22d3ee] outline-none" />
            </div>
            <button disabled={busy} type="submit" data-testid="admin-login-submit"
              className="w-full bg-[#22d3ee] text-[#0a1420] font-bold py-3 rounded-lg hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={18} className="animate-spin" /> : null}{busy ? "Signing in…" : "Log In"}
            </button>
          </form>
        </div>
        <p className="text-center text-slate-500 text-sm mt-6"><Link to="/" className="hover:text-[#22d3ee]">← Back to website</Link></p>
      </div>
    </div>
  );
}
